/**
 * @component SetMoney
 * @title 이체금액입력
 * @description 버튼금액입력
 * @properties SetMoneyProps
 * @author 박성준
 * @history
 *   [2026-01-30][박성준] 초기 작성
 */
import { forwardRef, RefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatUtils } from 'scbk-shared-module';
import { ButtonAmount, cn, PriceInput, useDevice } from 'scbk-ui-web';

/** 최소 입력 단위 허용값 */
export const MIN_AMOUNT_UNIT = {
  NONE: '0',
  HUNDRED: '100',
  THOUSAND: '1000',
  TEN_THOUSAND: '10000',
  HUNDRED_THOUSAND: '100000',
  MILLION: '10000000',
  TEN_MILLION: '100000000',
  HUNDRED_MILLION: '1000000000',
} as const;

/** 기본값 */
const DEFAULT_PRICE = '';

type AmountPreset = {
  /** 금액 */
  amount: string;
  /** 버튼에 표시되는 값 */
  label: string;
  /** 엘레멘트 고유 ID */
  id: string;
};

type SetMoneyProps = {
  initialPrice?: string;
  currency?: string;
  /** id가 all이면 최대한도로 세팅 */
  amountPresets: AmountPreset[];
  cls?: string;
  onChange?: (value: string) => void;
  /** 최대한도 */
  limit?: string;
  /** 최소입력단위 */
  minAmountUnit?: MinAmountUnit;
  /** labelFor id */
  id?: string;
  value?: string;
  enterKeyHint?: 'enter' | 'done' | 'go' | 'next' | 'previous' | 'search' | 'send' | undefined;
  nextInputRef?: RefObject<HTMLInputElement | null>;
  typeText?: boolean;
};

type MinAmountUnit = (typeof MIN_AMOUNT_UNIT)[keyof typeof MIN_AMOUNT_UNIT];

/** 숫자반환 */
const toNumberType = (value?: string | number): number => {
  const targetValue = value === null || value === undefined ? DEFAULT_PRICE : String(value);

  const parsedValue = Number(formatUtils.parseNumber(targetValue));

  if (Number.isNaN(parsedValue)) {
    return 0;
  }

  return parsedValue;
};

/** 최소입력단위 숫자반환 */
const getMinAmountUnitValue = (minAmountUnit?: MinAmountUnit): number => {
  return toNumberType(minAmountUnit ?? MIN_AMOUNT_UNIT.NONE);
};

/** 화면표시금액 */
const getDisplayPrice = (price: string, minAmountUnit?: MinAmountUnit): string => {
  if (isBlank(price)) {
    return '';
  }

  const unit = getMinAmountUnitValue(minAmountUnit);

  if (unit <= 0) {
    return String(toNumberType(price));
  }

  return String(toNumberType(price) / unit);
};

/** 실제금액 */
const getRealPrice = (displayPrice: string, minAmountUnit?: MinAmountUnit): string => {
  const unit = getMinAmountUnitValue(minAmountUnit);

  if (unit <= 0) {
    return String(toNumberType(displayPrice));
  }

  return String(toNumberType(displayPrice) * unit);
};

/** 최소입력단위 적용 */
const applyMinAmountUnit = (amount: number, minAmountUnit?: MinAmountUnit): number => {
  const unit = getMinAmountUnitValue(minAmountUnit);

  if (unit <= 0) {
    return amount;
  }

  return Math.floor(amount / unit) * unit;
};

/** 최대한도 적용 */
const applyLimitAmount = (amount: number, limit?: string): number => {
  const limitAmount = toNumberType(limit);

  if (limitAmount <= 0) {
    return amount;
  }
  return Math.min(amount, limitAmount);
};

/** 금액보정 */
const normalizeAmount = ({
  amount,
  limit,
  minAmountUnit,
}: {
  amount: number;
  limit?: string;
  minAmountUnit?: MinAmountUnit;
}): string => {
  const limitAppliedAmount = applyLimitAmount(amount, limit);
  const unitAppliedAmount = applyMinAmountUnit(limitAppliedAmount, minAmountUnit);

  return String(unitAppliedAmount);
};

const isBlank = (value?: unknown): boolean => {
  return (
    value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
  );
};

const SetMoney = forwardRef<HTMLInputElement, SetMoneyProps>(
  (
    {
      initialPrice = DEFAULT_PRICE,
      currency,
      amountPresets,
      cls = 'inline-flex flex-1 gap-1 flex-row flex-nowrap',
      onChange,
      limit,
      minAmountUnit,
      id = 'priceField',
      value,
      enterKeyHint = 'go',
      nextInputRef,
      typeText = false,
    },
    ref,
  ) => {
    /** 다국어 */
    const { t } = useTranslation();

    /** 큰 글씨 모드 */
    const { isLargeText, isDesktop } = useDevice();

    const [price, setPrice] = useState<string>(
      isBlank(initialPrice)
        ? ''
        : normalizeAmount({
            amount: toNumberType(initialPrice),
            limit,
            minAmountUnit,
          }),
    );

    const MIN_AMOUNT_UNIT_CURRENCY_MAP: Record<MinAmountUnit, string> = {
      [MIN_AMOUNT_UNIT.NONE]: t('SetMoneyComponent.002', {
        defaultValue: '원',
      }),
      [MIN_AMOUNT_UNIT.HUNDRED]: t('SetMoneyComponent.003', {
        defaultValue: '백원',
      }),
      [MIN_AMOUNT_UNIT.THOUSAND]: t('SetMoneyComponent.004', {
        defaultValue: '천원',
      }),
      [MIN_AMOUNT_UNIT.TEN_THOUSAND]: t('SetMoneyComponent.005', {
        defaultValue: '만원',
      }),
      [MIN_AMOUNT_UNIT.HUNDRED_THOUSAND]: t('SetMoneyComponent.006', {
        defaultValue: '십만원',
      }),
      [MIN_AMOUNT_UNIT.MILLION]: t('SetMoneyComponent.007', {
        defaultValue: '백만원',
      }),
      [MIN_AMOUNT_UNIT.TEN_MILLION]: t('SetMoneyComponent.008', {
        defaultValue: '천만원',
      }),
      [MIN_AMOUNT_UNIT.HUNDRED_MILLION]: t('SetMoneyComponent.009', {
        defaultValue: '억원',
      }),
    };

    const getCurrency = ({
      currency,
      minAmountUnit,
    }: {
      currency?: string;
      minAmountUnit?: MinAmountUnit;
    }): string => {
      if (currency) {
        return currency;
      }

      return (
        MIN_AMOUNT_UNIT_CURRENCY_MAP[minAmountUnit ?? MIN_AMOUNT_UNIT.NONE] ??
        t('SetMoneyComponent.002', {
          defaultValue: '원',
        })
      );
    };

    const handlePresetClick = (id: string, amount: string) => {
      if ('all' === id) {
        setPrice(
          normalizeAmount({
            amount: toNumberType(amount),
            limit: limit,
            minAmountUnit: minAmountUnit,
          }),
        );
        return;
      }

      if ('clear' === id) {
        setPrice(amount);
        return;
      }

      const nextAmount = toNumberType(price) + toNumberType(amount);

      setPrice(normalizeAmount({ amount: nextAmount, limit: limit, minAmountUnit: minAmountUnit }));
    };

    useEffect(() => {
      onChange?.(price);
    }, [price]);

    useEffect(() => {
      setPrice(
        isBlank(initialPrice)
          ? ''
          : normalizeAmount({
              amount: toNumberType(initialPrice),
              limit: limit,
              minAmountUnit: minAmountUnit,
            }),
      );
    }, [initialPrice, limit, minAmountUnit]);

    useEffect(() => {
      if (value) {
        setPrice(
          isBlank(value)
            ? ''
            : normalizeAmount({
                amount: toNumberType(value),
                limit: limit,
                minAmountUnit: minAmountUnit,
              }),
        );
      }
    }, [value]);

    return (
      <>
        <PriceInput
          id={id}
          ref={ref}
          currency={getCurrency({
            currency,
            minAmountUnit: minAmountUnit,
          })}
          typeText={typeText}
          value={isBlank(price) ? '' : formatUtils.toMoney(getDisplayPrice(price, minAmountUnit))}
          onChange={(value) => {
            if (isBlank(value)) {
              setPrice('');
              return;
            }
            const realPrice = getRealPrice(value, minAmountUnit);
            setPrice(
              normalizeAmount({
                amount: toNumberType(realPrice),
                limit: limit,
                minAmountUnit: minAmountUnit,
              }),
            );
          }}
          type="tel"
          inputMode="numeric"
          data-is-ext-e2e={true}
          aria-label={t('SetMoneyComponent.001', {
            defaultValue: '금액',
          })}
          title={t('SetMoneyComponent.010', {
            defaultValue: '금액 입력',
          })}
          placeholder={'0'}
          unitText={t('SetMoneyComponent.012', '영')}
          enterKeyHint={enterKeyHint ? enterKeyHint : nextInputRef?.current ? 'next' : 'go'}
          nextInputRef={nextInputRef}
        />

        <div className={cn(cls, isLargeText && !isDesktop && 'flex-wrap')}>
          {amountPresets.map((amountPreset, idx) => (
            <ButtonAmount
              className={`${amountPreset.id === 'clear' ? 'w-[calc(33.3%-4px)]' : isLargeText && 'w-[calc(33.3%-4px)]'}`}
              key={`${amountPreset.id}-${amountPreset.amount}-${idx}`}
              onClick={() => handlePresetClick(amountPreset.id, amountPreset.amount)}
            >
              {amountPreset.label}
            </ButtonAmount>
          ))}
        </div>
      </>
    );
  },
);
export { SetMoney };
export type { AmountPreset };
